/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { isNumeric } from 'validator';
import { FC, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Switch, Typography } from '@mui/material';
import ChatRow from './chat-row';
import ChatListHeader from './chat-header';
import { BORDER_COLOR } from '../../../../../shared/typings';
import {
  chatListAtom,
  isCustomFieldUpdatedAtom,
  showFiltersAtom,
  toggleSelectedChatIdsAtom,
  selectedChatIdsListAtom,
  isAllChatsSelectedAtom,
  excludedChatIdsAtom,
} from '../../states/chat-list';
import {
  ChatRowData,
  ChatSearchArgs,
  ChatType,
  SearchChat,
  SearchChatMessage,
  SearchChatPagination,
} from './chat-row/typings';
import usePaginatedQuery from '../../../../../shared/hooks/usePaginatedQuery';
import { getChatSearch } from '../../requests/get-chat';
import { viewedChatScreen } from '../../requests/viewed-chat-screen';
import Loader from '../../../../../shared/components/molecules/loader';
import { LOADING } from '../../../../../shared/constants';
import { GroupedVirtuoso, GroupedVirtuosoHandle, ListRange } from 'react-virtuoso';
import { ChatStatuses, ChatStatusFilters } from '../../typings';
import { QSButton, QSTypography } from '../../../../../shared/components/atoms';
import NewChatButton from './new-chat';
import ErrorIcon from '../../../../../shared/icons/Error';
import { ReactComponent as SearchClose } from '../../../../../shared/assets/svg/search-close.svg';
import useDoOnMount from '../../../../../shared/hooks/useDoOnMount';
import { trackChatScreenStart } from '../../../../../infra/analytics/utils';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useConversationSocket } from '../../hooks/use-conversation-socket';
import useChannelAccessSocket from '../../hooks/use-channel-access-socket';
import { CHAT_UPDATE_TYPE } from '../../typings/chat';
import SearchMessageRow from './search-message-row';
import { chatIdsAtom, chatListSetter, chatRowSetter, searchMessagesAtom } from '../../states/chat';
import { DemoAccountUserBarRenderer } from '../../../../../shared/modules/demo-account/v1/components';
import { useRole } from '../../../../../shared/hooks/use-role';
import { selectedChatAtom, selectedChatIdSetter } from '../../states/selected-chat';
import { conversationPageStateAtom } from '../../../../conversations/v1/states';
import FilterClearHeader from './chat-header/filters';
import AppliedFilters from './applied-filters';
import NewChatMain from './new-chat/main';
import useSingleWaba from '../../../../../shared/hooks/use-single-waba';
import { WabaChannelStatus } from '../../../../../shared/typings/waba';
import usePrevious from '../../../../../shared/hooks/usePrevious';
import { isEmbedAtom } from '../../../../embed/state';
import { useCustomNavigate } from '../../../../../shared/hooks/use-conversation-navigate';
import { useSearchParams } from 'react-router-dom';
import { customFieldsAtom } from '../../states/custom-fields';
import { customerDetailAtomFamily } from '../../states/customer-details';
import AiFilteringToggle from './ai-filtering';
import { isFilterAIAwaitingResponseEnabled } from './ai-filtering/state';
import CreateGroupModal from './new-chat/CreateGroupModal';
import { isCreateGroupModalOpenAtom } from '../../../../wa-groups/states/groups';
import AgentAvailabilityChatList from '../../../../agent-availability/aa-chatlist';
import WarningIcon from '../../../../../shared/icons/icons-revamp/warning';
import { subscribedPlanAtom } from '../../../../renew-plan/v1/states/subscribed-plan.atom';
import { ProductPlan } from '../../../../renew-plan/v1/typings';
import { aiFeaturePermissionAtom } from '../chat-input/state';
import { userProfileAtom } from '../../../../../shared/states/user';
import { getCountryFromCountryCode } from '../../../../../shared/utils/countries-mapper';

const Footer: FC<{
  context?:
    | {
        loading: boolean;
        error: boolean;
        fetchMore: () => void;
        isMessageSearch?: boolean;
        hasMore?: boolean;
        dataLength?: number;
        isLoading?: boolean;
      }
    | undefined;
}> = (props) => {
  const subscribedPlan = useAtomValue(subscribedPlanAtom);
  const loading = props?.context?.loading ?? false;
  const error = props?.context?.error ?? false;
  const isMessageSearch = props?.context?.isMessageSearch ?? false;
  const hasMore = props?.context?.hasMore ?? false;
  const dataLength = props?.context?.dataLength ?? 0;
  const isLoading = props?.context?.isLoading ?? false;

  if (loading) {
    return (
      <div
        css={css`
          padding: 3rem;
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 120px;
          flex-shrink: 0;
        `}
      >
        <Loader size="24px" />
      </div>
    );
  }

  if (error) {
    return (
      <div
        css={css`
          padding: 3rem;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 8px;
          min-height: 120px;
          flex-shrink: 0;
          box-sizing: border-box;
        `}
      >
        <WarningIcon size={24} color="#FF0000" />
        <QSTypography
          css={css`
            font-size: 16px;
            font-weight: 500;
            color: #585858;
            text-align: center;
            margin: 0;
          `}
        >
          Failed to load more {isMessageSearch ? 'messages' : 'chats'}.
        </QSTypography>
        <QSButton
          variant="text"
          onClick={props?.context?.fetchMore}
          css={css`
            font-size: 16px;
            font-weight: 500;
            color: #3f906d;
          `}
        >
          Retry
        </QSButton>
      </div>
    );
  }

  if (
    isMessageSearch &&
    !loading &&
    !hasMore &&
    dataLength > 0 &&
    !isLoading &&
    (subscribedPlan.data?.id === ProductPlan.PRO || subscribedPlan.data?.id === ProductPlan.ENTERPRISE)
  ) {
    return (
      <div
        css={css`
          padding: 20px;
          display: flex;
          justify-content: center;
          align-items: center;
        `}
      >
        <QSTypography
          css={css`
            color: #7a7a7a;
            font-size: 14px;
            text-align: center;
          `}
        >
          Showing results from the last 3 {subscribedPlan.data?.id === ProductPlan.ENTERPRISE ? 'years' : 'months'}
        </QSTypography>
      </div>
    );
  }

  return null;
};

const ChatListSection = memo(() => {
  const isEmbed = useAtomValue(isEmbedAtom);
  const subscribedPlan = useAtomValue(subscribedPlanAtom);
  const navigate = useCustomNavigate();
  const state = useAtomValue(chatListAtom);
  const [, setChatListState] = useAtom(chatListAtom);
  const [isNewChat, setIsNewChat] = useState(false);
  const setChatList = useSetAtom(chatListSetter);
  const [searchMessages, setSearchMessages] = useAtom(searchMessagesAtom);
  const { isDemoUser } = useRole();
  const [chatListRange, setChatListRange] = useState<ListRange | undefined>(undefined);
  const chatFilters = useAtomValue(showFiltersAtom);
  const [isCustomFieldUpdated, setIsCustomFieldUpdated] = useAtom(isCustomFieldUpdatedAtom);
  const isCreateGroupModalOpen = useAtomValue(isCreateGroupModalOpenAtom);
  const {
    searchTerm,
    assignedUserId,
    tags,
    conversationView,
    status: activeStatus,
    slaStatus,
    isSearchActive,
    customList,
    leadCustomFieldIdList,
    waba,
    tagIds,
    customFieldConditions,
  } = state;
  const trimmedSearchTerm = useMemo(() => searchTerm?.trim(), [searchTerm]);
  const userProfile = useAtomValue(userProfileAtom);
  const countryDialCode = getCountryFromCountryCode(userProfile?.countryCode ?? '')?.symbol ?? '+91';

  const setChatListRef = useRef(setChatList);
  const isSearchActiveRef = useRef(isSearchActive);
  const setSearchMessagesRef = useRef(setSearchMessages);
  const selectedChatId = useAtomValue(selectedChatIdSetter);
  const { isNewPage } = useAtomValue(conversationPageStateAtom);
  const previousSearchTerm = usePrevious<string>(searchTerm);
  const previousIsSearchActive = usePrevious<boolean>(isSearchActive);
  const previousLeadCustomFieldIdList = usePrevious(leadCustomFieldIdList);
  const chatListRef = useRef<GroupedVirtuosoHandle>(null);
  const [isChatLoaded, setIsChatLoaded] = useState(false);
  setChatListRef.current = setChatList;
  setSearchMessagesRef.current = setSearchMessages;
  isSearchActiveRef.current = isSearchActive;
  const setChatRow = useSetAtom(chatRowSetter);
  const setToggleSelectedChatIds = useSetAtom(toggleSelectedChatIdsAtom);
  const setSelectedChatIdsList = useSetAtom(selectedChatIdsListAtom);
  const setIsAllChatsSelected = useSetAtom(isAllChatsSelectedAtom);
  const setExcludedChatIds = useSetAtom(excludedChatIdsAtom);

  const selectedChat = useAtomValue(selectedChatAtom);

  const phone = selectedChat?.chatType === ChatType.INDIVIDUAL ? selectedChat.phoneNumber : '';
  const integrationWabaNumber = selectedChat?.integrationWabaNumber || '';

  const customerDetailAtom = useMemo(() => {
    return customerDetailAtomFamily({
      id: `${integrationWabaNumber}::${phone}`,
    });
  }, [integrationWabaNumber, phone]);

  const { data: customerDetails } = useAtomValue(customerDetailAtom);
  const filterAIAwaitingResponse = useAtomValue(isFilterAIAwaitingResponseEnabled);

  const aiFeaturePermissions = useAtomValue(aiFeaturePermissionAtom);
  const shouldShowAgentAvailability = aiFeaturePermissions?.permissions?.agent_availability;

  useChannelAccessSocket();
  // useConversationSocket();
  useDoOnMount(trackChatScreenStart);

  const { hasMultipleWabas: showWaba } = useSingleWaba(WabaChannelStatus.ACTIVE);

  const abortControllerRef = useRef<AbortController | null>(null);

  // Call API when component mounts (chat screen first opens) and when filters change
  // useEffect(() => {
  //   viewedChatScreen().catch((error) => {
  //     console.error('Failed to call viewed-chat-screen API:', error);
  //   });
  // }, [assignedUserId, tags, waba, customList, searchTerm, leadCustomFieldIdList]);

  const getter = useCallback(
    async (paginationData?: SearchChatPagination, args: ChatSearchArgs = {}) => {
      const { messageExists, nextChatId, nextChatTimestamp, nextMessageId, nextMessageScore, nextMessageTimestamp } =
        paginationData ?? {};

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const controller = new AbortController();
      abortControllerRef.current = controller;

      const { signal } = controller;
      let mergedCustomList = customList;
      if (leadCustomFieldIdList?.listId) {
        mergedCustomList = {
          ...mergedCustomList,
          ...{ [leadCustomFieldIdList.listId]: leadCustomFieldIdList?.fieldId },
        };
      }

      let chats: SearchChat[] = [];
      let chatMessages: SearchChatMessage[] = [];
      // Useful for message pagination mode: preserve the exhausted chat cursor (avoid calling /v3/search again)
      let paginationOptions: SearchChatPagination = {
        messageExists: false,
        nextChatId: undefined,
        nextChatTimestamp: undefined,
        nextMessageId: undefined,
        nextMessageScore: undefined,
        nextMessageTimestamp: undefined,
      };

      const effectiveSearchTerm = args.searchTerm || searchTerm;
      const isSearching = typeof effectiveSearchTerm === 'string' && effectiveSearchTerm.length > 0;
      const chatType = conversationView === 'groups' ? 'WA_GROUP' : undefined;

      // parent function run on scroll end and we need to add check if more search in chats are available
      if (!paginationData || (typeof nextChatId === 'string' && nextChatId.length > 0)) {
        const response = await getChatSearch({
          messageExists: false, // force chats-only behavior from `/v3/search` api
          nextChatId,
          nextChatTimestamp,
          limit: 30,
          assignedUserId,
          search: effectiveSearchTerm,
          status: activeStatus,
          slaStatus,
          customList: mergedCustomList,
          tags: tags,
          waba,
          signal,
          tagIds,
          filterAIAwaitingResponse,
          chatType,
          customFieldConditions,
        });

        chats = response.chats;
        paginationOptions = {
          ...paginationOptions,
          nextChatId: response.paginationOptions.nextChatId ?? undefined,
          nextChatTimestamp: response.paginationOptions.nextChatTimestamp ?? undefined,
        };
      }

      // parent function run on scroll end and we need to add check if more search in messages are available
      // chats exhausted => start /v1/chat/search message pagination
      if (isSearching && !paginationOptions.nextChatId) {
        const response = await getChatSearch({
          onlyMsgSearch: true,
          messageExists,
          nextMessageId,
          nextMessageScore,
          nextMessageTimestamp,
          limit: 30,
          assignedUserId,
          search: effectiveSearchTerm,
          status: activeStatus,
          slaStatus,
          customList: mergedCustomList,
          tags: tags,
          waba,
          signal,
          tagIds,
          filterAIAwaitingResponse,
          chatType,
          customFieldConditions,
        });

        chatMessages = chatMessages.concat(response.chatMessages ?? []);
        paginationOptions = {
          ...paginationOptions,
          messageExists: response.paginationOptions.messageExists,
          nextMessageId: response.paginationOptions.nextMessageId,
          nextMessageScore: response.paginationOptions.nextMessageScore,
          nextMessageTimestamp: response.paginationOptions.nextMessageTimestamp,
        };
      }

      setIsChatLoaded(true);

      if (isSearchActiveRef.current) {
        setSearchMessagesRef.current((prevMessages) =>
          paginationData ? [...prevMessages, ...chatMessages] : chatMessages
        );
      }

      setChatListRef.current({
        type: paginationData ? CHAT_UPDATE_TYPE.UPDATE : CHAT_UPDATE_TYPE.ADD,
        data: chats as ChatRowData[],
      });

      return {
        data: chats.map(({ id }) => id),
        paginationData: {
          ...paginationOptions,
          hasMore:
            (!!paginationOptions?.nextChatId && !!paginationOptions?.nextChatTimestamp) ||
            (!!paginationOptions?.nextMessageId && !!paginationOptions?.nextMessageTimestamp),
        },
      };
    },
    [
      assignedUserId,
      trimmedSearchTerm,
      activeStatus,
      slaStatus,
      customList,
      leadCustomFieldIdList,
      tags,
      waba,
      tagIds,
      conversationView,
      setIsChatLoaded,
      filterAIAwaitingResponse,
      customFieldConditions,
    ]
  );

  const {
    requestData: chatRowData,
    fetchMore,
    refresh,
  } = usePaginatedQuery<string, SearchChatPagination>({
    getter: getter,
    requestAtom: chatIdsAtom,
  });

  useEffect(() => {
    if (previousSearchTerm !== searchTerm) {
      // When clearing search, clear both chats + messages immediately (same as message list behavior)
      if (previousSearchTerm && !searchTerm) {
        setSearchMessages([]);
        setChatListRef.current({ type: CHAT_UPDATE_TYPE.DELETE });
        setIsChatLoaded(false);
      }
      refresh({ searchTerm });
      return;
    }
    let shouldRefresh = false;
    let mergedCustomList = customList;
    if (previousLeadCustomFieldIdList !== leadCustomFieldIdList || (isCustomFieldUpdated && leadCustomFieldIdList)) {
      if (leadCustomFieldIdList?.listId) {
        mergedCustomList = {
          ...mergedCustomList,
          ...{ [leadCustomFieldIdList.listId]: leadCustomFieldIdList?.fieldId },
        };
      }
      shouldRefresh = true;
    }
    if (isCustomFieldUpdated && customFieldConditions && customFieldConditions.length > 0) {
      shouldRefresh = true;
    }
    if (shouldRefresh) {
      refresh({ searchTerm, customList: mergedCustomList });
      setIsCustomFieldUpdated(false);
    }
  }, [
    trimmedSearchTerm,
    refresh,
    previousSearchTerm,
    previousLeadCustomFieldIdList,
    leadCustomFieldIdList,
    customList,
    isCustomFieldUpdated,
    customFieldConditions,
  ]);

  useEffect(() => {
    const eventListenerCallback = () => {
      refresh();
    };
    document.addEventListener('resetChatList', eventListenerCallback);

    return () => {
      document.removeEventListener('resetChatList', eventListenerCallback);
    };
  }, [refresh]);

  const findSelectedChatIndex = useCallback(() => {
    if (!selectedChatId || !chatRowData.data) return -1;
    const index = chatRowData.data.indexOf(selectedChatId);
    return index;
  }, [chatRowData.data, selectedChatId]);

  const scrollChat = useCallback((index: number) => {
    if (chatListRef.current) {
      chatListRef.current.scrollToIndex({
        index,
        behavior: 'auto',
      });
    }
  }, []);

  useEffect(() => {
    if (isChatLoaded && selectedChatId) {
      setChatRow({
        type: CHAT_UPDATE_TYPE.UPDATE,
        partial: true,
        data: {
          id: selectedChatId,
          selected: true,
        },
        reOrder: false,
      });
    }
  }, [isChatLoaded, selectedChatId]);

  useEffect(() => {
    if (isNewPage) {
      const index = findSelectedChatIndex();
      scrollChat(index);
    }
  }, [isNewPage]);

  useEffect(() => {
    if (
      isChatLoaded &&
      !chatRowData.loading &&
      !chatRowData.refreshing &&
      (!chatRowData.data || chatRowData.data.length === 0)
    ) {
      setToggleSelectedChatIds(false);
      setSelectedChatIdsList([]);
      setIsAllChatsSelected(false);
      setExcludedChatIds([]);
    }
  }, [
    chatRowData.data,
    chatRowData.loading,
    chatRowData.refreshing,
    isChatLoaded,
    setToggleSelectedChatIds,
    setSelectedChatIdsList,
    setIsAllChatsSelected,
    setExcludedChatIds,
  ]);

  useEffect(() => {
    // When search is closed, clear both messages and chats and then refresh.
    if (previousIsSearchActive && !isSearchActive) {
      setSearchMessages([]);
      setChatListRef.current({ type: CHAT_UPDATE_TYPE.DELETE });
      setIsChatLoaded(false);
      refresh({ searchTerm });
    }
  }, [isSearchActive, previousIsSearchActive, refresh, searchTerm, setSearchMessages]);

  useEffect(() => {
    refresh({
      searchTerm,
    });

    if (chatListRef.current) {
      chatListRef.current.scrollToIndex({
        index: 0,
        behavior: 'auto',
      });
    }
    setChatListRange(undefined);
  }, [activeStatus, assignedUserId, waba, tags, conversationView]);

  const rowRenderer = useCallback(
    (index: number, groupIndex: number) => {
      if (isSearchActive && searchTerm && groupIndex === 1) {
        const absoluteIndex = index - (chatRowData.data ?? []).length;

        const message = searchMessages[absoluteIndex];

        if (typeof message?.id !== 'string' || message?.id?.length === 0) {
          return null;
        }

        return (
          <div
            key={message.id}
            id={message.id}
            css={css`
              border-bottom: 1px solid rgba(224, 224, 234, 0.57);
              height: fit-content;
            `}
          >
            <SearchMessageRow data={message} />
          </div>
        );
      }

      if (!chatRowData.data || typeof chatRowData.data[index] !== 'string' || chatRowData.data[index].length === 0) {
        return null;
      }

      const id = chatRowData.data[index] ?? '';

      return (
        <div
          key={id}
          id={id}
          css={css`
            height: fit-content;
          `}
        >
          <ChatRow id={id} showWaba={showWaba} />
        </div>
      );
    },
    [chatRowData.data, isSearchActive, searchMessages, trimmedSearchTerm, showWaba]
  );

  const groupHeaderRenderer = useCallback(
    (index: number) => {
      if (index === 1 && isSearchActive && searchTerm) {
        return (
          <div
            css={css`
              padding: 12px 24px;
              height: fit-content;
              background-color: white;
              border-bottom: 1px solid rgb(224, 224, 234);
            `}
          >
            <QSTypography>MESSAGES</QSTypography>
          </div>
        );
      }

      // This heading can not be removed as element of height zero is not allowed in virtual list
      // hence do not remove margin-top: -1 from virtual list that is to override the this height
      return (
        <div
          css={css`
            height: 1px;
          `}
        />
      );
    },
    [isSearchActive, trimmedSearchTerm]
  );

  const [searchParams, setSearchParams] = useSearchParams();

  const handleSwitchToAllChats = () => {
    searchParams.set('status', 'all');
    searchParams.set('slaStatus', 'non-breached');
    setSearchParams(searchParams);
  };

  const handleClearSearch = () => {
    setChatListState((prev) => ({
      ...prev,
      searchTerm: '',
    }));
  };

  const renderNoResultsFound = () => {
    const activeStatusLabel = ChatStatusFilters.find((option) => option.value === activeStatus)?.name || 'selected';
    const isPhoneNumberLike =
      !!searchTerm && isNumeric(searchTerm) && searchTerm.length >= 7 && searchTerm.length <= 15;
    const showCountryCodeSuggestion = !chatRowData?.loading && activeStatusLabel === 'All' && isPhoneNumberLike;
    return (
      <div
        css={css(`
        padding: 30px 25px;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 10px;
        `)}
      >
        <SearchClose width={40} height={40} css={css(`margin-bottom: 15px;`)} />
        <QSTypography>
          No results found in {activeStatusLabel} chats list{' '}
          {(subscribedPlan.data?.id === ProductPlan.PRO || subscribedPlan.data?.id === ProductPlan.ENTERPRISE) &&
            `in last 3 ${subscribedPlan.data?.id === ProductPlan.ENTERPRISE ? 'years' : 'months'}`}
        </QSTypography>

        {activeStatusLabel !== 'All' && (
          <>
            <Typography
              css={css`
                color: #7a7a7a;
                font-size: 12px;
                font-weight: 400;
                margin-bottom: 15px;
              `}
            >
              Try searching in{' '}
              <span
                css={css`
                  font-weight: 600;
                `}
              >
                All chats list
              </span>{' '}
              to see matching results
              {isPhoneNumberLike && (
                <>
                  , or with the country code (e.g. {countryDialCode}
                  {searchTerm})
                </>
              )}
            </Typography>
            <Button
              variant="contained"
              css={css`
                width: 100%;
              `}
              onClick={handleSwitchToAllChats}
            >
              Switch to All Chats
            </Button>
          </>
        )}
        {showCountryCodeSuggestion && searchTerm && (
          <Typography
            css={css`
              color: #7a7a7a;
              font-size: 12px;
              font-weight: 400;
            `}
          >
            Try searching with the country code (e.g. {countryDialCode}
            {searchTerm})
          </Typography>
        )}
        {searchTerm && (
          <Button
            variant="outlined"
            css={css`
              width: 100%;
              border: 1px solid #e0e0e9;
            `}
            color="inherit"
            onClick={handleClearSearch}
          >
            Clear Search
          </Button>
        )}
      </div>
    );
  };

  const renderChatListContent = () => {
    if (!isChatLoaded && (chatRowData.loading || chatRowData.refreshing)) {
      return <Loader secondary={LOADING} />;
    }

    if ((!chatRowData.data || chatRowData.data?.length < 1) && searchMessages.length < 1) {
      return (
        <div
          css={css(`
            text-align:center
            `)}
        >
          {(typeof searchTerm === 'string' && searchTerm.length > 0) ||
          (typeof assignedUserId === 'string' && assignedUserId.length > 0) ||
          (!!tags && tags.length > 0) ||
          (typeof activeStatus === 'string' && activeStatus.length > 0) ||
          Object.keys(customList || {}).length > 0 ||
          (!!waba && waba.length > 0) ||
          slaStatus === 'breached' ? (
            <>{renderNoResultsFound()}</>
          ) : (
            <>
              <ErrorIcon color={'#6a6e73'} css={css(`margin-top: 40px;`)} />
              <QSTypography>'No customer has been assigned'</QSTypography>
            </>
          )}
        </div>
      );
    }

    if (isNewChat) {
      return null;
    }

    const groupCounts = [chatRowData.data?.length || 0];

    if (searchMessages.length) {
      groupCounts.push(searchMessages.length);
    }

    const handleRangeChange = ({ startIndex, endIndex }: { startIndex: number; endIndex: number }) => {
      setChatListRange({ startIndex, endIndex });

      const chatsCount = chatRowData.data?.length ?? 0;
      const messagesCount = searchMessages.length;
      const totalItems = chatsCount + messagesCount;

      // Nothing to paginate
      if (totalItems === 0 || endIndex < 0) return;

      // Don't fetch more if there's an error
      if (chatRowData.error) return;

      const remaining = totalItems - 1 - endIndex; // endIndex is inclusive
      if (remaining < 2 && !chatRowData.isFetchingMore && chatRowData.paginationData?.hasMore) {
        fetchMore();
      }
    };

    // Determine which API failed based on pagination state
    // If nextChatId doesn't exist but we're searching, we're in message search mode (v1/chat/search)
    // Otherwise, we're in chat search mode (v3/search)
    const isMessageSearchMode =
      typeof searchTerm === 'string' && searchTerm.length > 0 && !chatRowData.paginationData?.nextChatId;

    const chatsCount = chatRowData.data?.length ?? 0;
    const messagesCount = searchMessages.length;
    const totalItems = chatsCount + messagesCount;

    return (
      <GroupedVirtuoso
        context={{
          loading: chatRowData.isFetchingMore ?? false,
          error: !!chatRowData.error,
          fetchMore,
          isMessageSearch: isMessageSearchMode,
          hasMore: chatRowData.paginationData?.hasMore ?? false,
          dataLength: totalItems,
          isLoading: chatRowData.loading ?? false,
        }}
        groupCounts={groupCounts}
        groupContent={groupHeaderRenderer}
        itemContent={rowRenderer}
        ref={chatListRef}
        endReached={() => {
          if (!chatRowData.error && !chatRowData.isFetchingMore) {
            fetchMore();
          }
        }}
        initialTopMostItemIndex={Math.min(
          chatListRange?.startIndex || 0,
          Math.max((chatRowData.data?.length || 0) + searchMessages.length - 1, 0)
        )}
        increaseViewportBy={{ top: 0, bottom: 100 }}
        rangeChanged={handleRangeChange}
        style={{
          height: '100%',
        }}
        components={{ Footer }}
        css={css`
          margin-top: -1px;
          overflow-x: hidden;

          ${chatRowData.data &&
          chatRowData.data?.length > 8 &&
          `
            overflow-y: hidden !important;

            &:hover {
            overflow-y: auto !important;

            .chat-list-btn {
              padding-right: 8px !important;
              transition: none;
            }
          }
            `}
        `}
      />
    );
  };

  if (isNewChat) {
    return (
      <Box
        css={css`
          position: relative;
          height: 100%;
          width: ${chatFilters.open ? '320px' : '375px'};
          min-width: ${chatFilters.open ? '320px' : '375px'};
          max-width: ${chatFilters.open ? '320px' : '375px'};
          overflow: hidden;
          border-right: 1px solid ${BORDER_COLOR};
          transition: width 280ms ease-in-out, min-width 280ms ease-in-out, max-width 280ms ease-in-out;
        `}
      >
        <NewChatMain setNewChat={setIsNewChat} />
      </Box>
    );
  }

  return (
    <Box
      css={css`
        position: relative;
        height: 100%;
        width: ${chatFilters.open ? '320px' : '375px'};
        min-width: ${chatFilters.open ? '320px' : '375px'};
        max-width: ${chatFilters.open ? '320px' : '375px'};
        overflow: hidden;
        transition: width 280ms ease-in-out, min-width 280ms ease-in-out, max-width 280ms ease-in-out;
      `}
    >
      <Box
        sx={{
          height: '100%',
          width: '100%',
          borderRight: '1px solid rgba(224, 224, 234, 1)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <DemoAccountUserBarRenderer />
        <ChatListHeader showLoading={isChatLoaded && (chatRowData.loading || chatRowData.refreshing)} />
        {shouldShowAgentAvailability && <AgentAvailabilityChatList />}
        {activeStatus === ChatStatuses.WAITING_FOR_RESPONSE && <AiFilteringToggle />}
        <AppliedFilters />
        <div
          css={css`
            flex: 1;
            min-height: 0;
            overflow: hidden;
          `}
        >
          {renderChatListContent()}
        </div>
      </Box>
      {!isDemoUser && !isNewChat && <NewChatButton setNewChat={setIsNewChat} isNewChat={isNewChat} />}
      {isCreateGroupModalOpen && <CreateGroupModal />}
    </Box>
  );
});

export default ChatListSection;
